import { Loader2, Star } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useStars, useToggleStar } from '@/hooks/useStars';
import type { StarTarget } from '@/lib/nip25';

interface StarButtonProps {
  event: NostrEvent;
  /** `kind:pubkey:d` coordinate, required when `event.kind` is addressable (30000-39999). */
  addressPointer?: string;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * A "star" button for wiki contributions, backed by NIP-25 reactions
 * (`kind:7`, `content: '+'`) and NIP-09 deletion requests for unstarring.
 */
export function StarButton({ event, addressPointer, size = 'sm', className }: StarButtonProps) {
  const { user } = useCurrentUser();
  const target: StarTarget = { id: event.id, pubkey: event.pubkey, kind: event.kind, addressPointer };
  const { data, isLoading } = useStars(target);
  const { mutate: toggleStar, isPending } = useToggleStar(target, data?.userReaction);

  const starred = Boolean(data?.userReaction);
  const count = data?.count ?? 0;

  return (
    <Button
      type="button"
      variant={starred ? 'default' : 'outline'}
      size={size}
      disabled={!user || isPending}
      onClick={() => toggleStar()}
      title={
        !user
          ? 'Log in to star this contribution'
          : starred
            ? 'Remove your star'
            : 'Star this contribution'
      }
      className={cn('gap-1.5', className)}
    >
      {isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <Star className={cn('size-4', starred && 'fill-current')} />
      )}
      {isLoading ? '' : count}
    </Button>
  );
}
